import { Layout } from '@/client/layouts/layout';
import { StatusBadge } from '@/client/components/tables/live-streams-columns';
import { PlayerButton } from '@/client/components/player-button';
import { CCButton } from '@/client/components/cc-button';
import {
  DetailHeader,
  DetailCard,
  DetailGrid,
  DetailList,
  DetailRow,
  DetailLink,
  CopyableText,
  BooleanBadge,
  formatDateTime,
} from '@/client/components/ui/detail-view';
import { Button } from '@/client/components/ui/button';
import { RefreshCw, Loader2, ScrollText, ArrowRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';

/**
 * Unified stream data interface for both live and upcoming streams.
 * Not all fields are required - component renders conditionally based on what's available.
 */
export interface StreamDetailData {
  // Core identifiers
  clientID: string | null;
  clientName: string | null;
  eventID: string | null;
  eventTitle: string | null;
  status: string | null;
  streamStatus: string | null;

  // Schedule
  eventStartDateTime: string | null;
  eventEndDateTime: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  autoStart: boolean;
  autoStop: boolean;
  preroll: number;

  // Metadata
  customID: string | null;
  description: string | null;
  notes: string | null;
  private: boolean;
  liveToBreak: boolean;

  // Infrastructure (live streams only)
  encoderID: string | null;
  liveID?: string | null;
  ingInstanceID?: string | null;
  egInstanceID?: string | null;
  wcStreamID?: string | null;

  // URIs
  streamingURIs?: {
    main: string | null;
    backup: string | null;
  } | null;

  // Live stats (from WebSocket, live streams only)
  liveStats?: {
    runtime: string;
    status: string;
  } | null;
}

interface StreamDetailViewProps {
  stream: StreamDetailData | null;
  loading: boolean;
  backTo: string;
  backLabel: string;
  onRefresh?: () => void;
  /**
   * Composite stream ID in format clientID_eventID (used for player URL and display)
   */
  streamId: string;
  /**
   * Runtime value (pre-computed by parent component)
   */
  runtimeValue?: string | null;
  /**
   * Stream status value (pre-computed by parent component)
   */
  streamStatusValue?: string | null;
  /**
   * Whether this is a live stream (shows infrastructure section)
   */
  isLiveStream?: boolean;
  /**
   * Additional cards to render in the detail grid (e.g., caption pipeline card)
   */
  extraCards?: React.ReactNode;
  /**
   * Whether this event had live AI captions (shows badge in Event Info)
   */
  hasAiCaptions?: boolean;
}

/**
 * Shared detail view for live and upcoming streams.
 * Renders event info, schedule, streaming URIs, infrastructure, and metadata.
 */
export function StreamDetailView({
  stream,
  loading,
  backTo,
  backLabel,
  onRefresh,
  streamId,
  runtimeValue,
  streamStatusValue,
  isLiveStream = false,
  extraCards,
  hasAiCaptions,
}: StreamDetailViewProps) {
  // Parse clientID and eventID from streamId
  const [clientID, eventID] = (() => {
    const parts = streamId.split('_');
    if (parts.length >= 2) {
      return [parts[0], parts.slice(1).join('_')];
    }
    return [null, streamId];
  })();

  // Loading state
  if (loading && !stream) {
    return (
      <Layout>
        <DetailHeader
          title="Loading..."
          backTo={backTo}
          backLabel={backLabel}
        />
        <div className="rounded-lg border p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-4">Loading stream data...</p>
        </div>
      </Layout>
    );
  }

  // Not found state
  if (!stream) {
    return (
      <Layout>
        <DetailHeader
          title="Stream Not Found"
          backTo={backTo}
          backLabel={backLabel}
        />
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground mb-4">
            This stream may have ended or the data is no longer available.
          </p>
          <Button asChild variant="outline">
            <a href={backTo}>Return to streams</a>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <DetailHeader
        title={stream.eventTitle || 'Untitled Event'}
        subtitle={
          stream.clientID && (
            <DetailLink to="/clients/$clientID" params={{ clientID: stream.clientID }}>
              {stream.clientName || stream.clientID}
            </DetailLink>
          )
        }
        backTo={backTo}
        backLabel={backLabel}
        badge={<StatusBadge status={stream.status} />}
        actions={
          <div className="flex items-center gap-2">
            <PlayerButton clientID={clientID} eventID={eventID} />
            <CCButton clientID={clientID} eventID={eventID} clientName={stream.clientName} />
            {onRefresh && (
              <Button variant="ghost" size="icon" title="Refresh" onClick={onRefresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        }
      />

      <DetailGrid columns={2}>
        {/* Event Info */}
        <DetailCard title="Event Info">
          <DetailList>
            <DetailRow
              label="Event ID"
              value={<CopyableText text={streamId} />}
            />
            <DetailRow
              label="Client"
              value={
                stream.clientID ? (
                  <DetailLink to="/clients/$clientID" params={{ clientID: stream.clientID }}>
                    {stream.clientName || stream.clientID}
                  </DetailLink>
                ) : null
              }
            />
            {stream.customID && (
              <DetailRow label="Custom ID" value={<CopyableText text={stream.customID} />} />
            )}
            <DetailRow label="Private" value={<BooleanBadge value={stream.private} />} />
            <DetailRow label="Live to Break" value={<BooleanBadge value={stream.liveToBreak} />} />
            {hasAiCaptions !== undefined && (
              <DetailRow label="AI Captions" value={<BooleanBadge value={hasAiCaptions} />} />
            )}

            <DetailRow
              label="Logs"
              value={
                <Link
                  to="/pipeline-logs"
                  search={{ searchType: 'eventID', searchValue: eventID || streamId }}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ScrollText className="h-3 w-3" />
                  View pipeline logs
                </Link>
              }
            />
          </DetailList>
        </DetailCard>

        {/* Schedule */}
        <DetailCard title="Schedule">
          <DetailList>
            <DetailRow label="Auto Start" value={<BooleanBadge value={stream.autoStart} />} />
            <DetailRow label="Auto Stop" value={<BooleanBadge value={stream.autoStop} />} />
            <DetailRow
              label="Scheduled Start"
              value={formatDateTime(stream.eventStartDateTime)}
              mono
            />
            {isLiveStream && (
              <DetailRow
                label="Started Streaming"
                value={formatDateTime(stream.startDateTime)}
                mono
              />
            )}
            <DetailRow
              label="Scheduled End"
              value={formatDateTime(stream.eventEndDateTime)}
              mono
            />
            <DetailRow label="Preroll" value={`${stream.preroll}s`} mono />
            {runtimeValue && (
              <DetailRow label="Runtime" value={runtimeValue} mono />
            )}
            {streamStatusValue && (
              <DetailRow label="Stream Status" value={streamStatusValue} />
            )}
          </DetailList>
        </DetailCard>

        {/* Streaming URIs - only show if we have them */}
        {stream.streamingURIs && (stream.streamingURIs.main || stream.streamingURIs.backup) && (
          <DetailCard title="Streaming URIs">
            <div className="space-y-3">
              {stream.streamingURIs.main && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Main</p>
                  <CopyableText text={stream.streamingURIs.main} className="text-xs break-words text-left" />
                </div>
              )}
              {stream.streamingURIs.backup && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Backup</p>
                  <CopyableText text={stream.streamingURIs.backup} className="text-xs break-words text-left" />
                </div>
              )}
            </div>
          </DetailCard>
        )}

        {/* Infrastructure - only show for live streams */}
        {isLiveStream && (
          <DetailCard title="Infrastructure">
            <DetailList>
              <DetailRow
                label="Encoder"
                value={
                  stream.encoderID ? (
                    <DetailLink to="/encoders/$encoderID" params={{ encoderID: stream.encoderID }}>
                      <span className="font-mono inline-flex items-center gap-1 hover:text-foreground group hover:text-zinc-500">
                        {stream.encoderID}
                        <ArrowRight className="h-3 w-3 opacity-40 group-hover:opacity-70" />
                      </span>
                    </DetailLink>
                  ) : null
                }
              />
              <DetailRow
                label="Live ID"
                value={stream.liveID ? <CopyableText text={stream.liveID} truncate /> : null}
              />
              <DetailRow
                label="Ingress Instance"
                value={stream.ingInstanceID ? (
                  <DetailLink to="/aws/ec2/$instanceId" params={{ instanceId: stream.ingInstanceID }}>
                    <span className="font-mono inline-flex items-center gap-1 hover:text-foreground group hover:text-zinc-500">
                      {stream.ingInstanceID}
                      <ArrowRight className="h-3 w-3 opacity-40 group-hover:opacity-70" />
                    </span>
                  </DetailLink>
                ) : null}
              />
              <DetailRow
                label="Egress Instance"
                value={stream.egInstanceID ? (
                  <DetailLink to="/aws/ec2/$instanceId" params={{ instanceId: stream.egInstanceID }}>
                    <span className="font-mono inline-flex items-center gap-1 hover:text-foreground group hover:text-zinc-500">
                      {stream.egInstanceID}
                      <ArrowRight className="h-3 w-3 opacity-40 group-hover:opacity-70" />
                    </span>
                  </DetailLink>
                ) : null}
              />
              <DetailRow
                label="Wowza Stream ID"
                value={stream.wcStreamID ? <CopyableText text={stream.wcStreamID} /> : null}
              />
            </DetailList>
          </DetailCard>
        )}

        {/* Encoder for upcoming streams (simpler, no infrastructure) */}
        {!isLiveStream && stream.encoderID && (
          <DetailCard title="Assignment">
            <DetailList>
              <DetailRow
                label="Encoder"
                value={
                  <DetailLink to="/encoders/$encoderID" params={{ encoderID: stream.encoderID }}>
                    <span className="font-mono inline-flex items-center gap-1 hover:text-foreground group hover:text-zinc-500">
                      {stream.encoderID}
                      <ArrowRight className="h-3 w-3 opacity-40 group-hover:opacity-70" />
                    </span>
                  </DetailLink>
                }
              />
            </DetailList>
          </DetailCard>
        )}

        {/* Extra cards (e.g., caption pipeline) */}
        {extraCards}
      </DetailGrid>

      {/* Metadata - full width */}
      {(stream.description || stream.notes) && (
        <div className="mt-4">
          <DetailCard title="Metadata">
            <div className="space-y-4">
              {stream.description && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Description</h4>
                  <div
                    className="text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 break-words overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: stream.description }}
                  />
                </div>
              )}
              {stream.notes && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Notes</h4>
                  <p className="text-sm whitespace-pre-wrap break-words overflow-hidden">{stream.notes}</p>
                </div>
              )}
            </div>
          </DetailCard>
        </div>
      )}
    </Layout>
  );
}
